/**
 * Admin V2 API Slice - Comprehensive Admin Interface RTK Query Integration
 * Covers all 53 admin endpoints
 *
 * Categories:
 * - Dashboard & Analytics (7 APIs)
 * - User Management (6 APIs)
 * - Vendor Management (6 APIs)
 * - Buyer Management (6 APIs)
 * - Product Management (5 APIs)
 * - Category Management (7 APIs)
 * - Listings Management (9 APIs) ✅ NEW
 * - System Settings (7 APIs)
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Helper function to map frontend period filters to backend-compatible periods
const mapPeriodToBackend = (period) => {
  const periodMap = {
    '24h': 'day',
    '7d': 'week', 
    '30d': 'month',
    '90d': 'quarter',
    '365d': 'year',
    // Direct mappings
    'day': 'day',
    'week': 'week',
    'month': 'month',
    'quarter': 'quarter',
    'year': 'year'
  };
  
  return periodMap[period] || 'month';
};

// Base URL for admin API endpoints
const ADMIN_API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api/v1';

const adminBaseQuery = fetchBaseQuery({
  baseUrl: `${ADMIN_API_BASE_URL}/admin`,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    // Don't set content-type - let browser handle it automatically
    // JSON requests will get 'application/json' by RTK Query
    // FormData requests will get 'multipart/form-data' with boundary
    return headers;
  },
});

// Enhanced base query with error handling for admin operations
const adminBaseQueryWithErrorHandling = async (args, api, extraOptions) => {
  let result = await adminBaseQuery(args, api, extraOptions);

  // Handle 401 errors by delegating to main API slice's reauth logic
  if (result.error?.status === 401) {
    // Let the main apiSlice handle token refresh
    api.dispatch({ type: 'auth/logout' });
  }

  return result;
};

export const adminApiV2Slice = createApi({
  reducerPath: 'adminApiV2',
  baseQuery: adminBaseQueryWithErrorHandling,
  tagTypes: [
    // Core entity tags
    'AdminDashboard',
    'AdminAnalytics',
    'AdminUsers',
    'AdminVendors',
    'AdminBuyers',
    'AdminProducts',
    'AdminCategories',
    'AdminMarkets',
    'AdminListings',
    // Specific workflow tags
    'UserManagement',
    'VendorVerification',
    'BuyerVerification',
    'BusinessVerification',
    'ContentModeration',
    'SystemSettings',
    'PerformanceMetrics',
    'SalesAnalytics',
    'UserAnalytics',
    'ProductAnalytics',
    'CategoryUsage',
    'MarketUsage',
  ],
  endpoints: (builder) => ({
    // ============================================
    // 1. DASHBOARD & ANALYTICS ENDPOINTS (7 APIs)
    // ============================================


    // Comprehensive dashboard overview with detailed statistics
    getDashboardOverview: builder.query({
      query: (params = {}) => ({
        url: 'dashboard/overview',
        params: {
          ...params,
          period: mapPeriodToBackend(params.period || 'month'),
        },
      }),
      providesTags: ['AdminDashboard'],
      // Polling disabled for MVP
    }),

    // Analytics overview with business intelligence metrics
    getAnalyticsOverview: builder.query({
      query: (params = {}) => ({
        url: 'analytics/overview',
        params: {
          period: mapPeriodToBackend(params.period || 'month'),
          startDate: params.startDate,
          endDate: params.endDate,
          ...params,
        },
      }),
      providesTags: ['AdminAnalytics'],
      // Cache analytics for 15 minutes
      keepUnusedDataFor: 900,
    }),

    // Sales analytics with revenue and transaction insights
    getSalesAnalytics: builder.query({
      query: (params = {}) => ({
        url: 'analytics/sales',
        params: {
          period: mapPeriodToBackend(params.period || 'month'),
          groupBy: params.groupBy || 'day',
          vendor: params.vendor,
          category: params.category,
          ...params,
        },
      }),
      providesTags: ['SalesAnalytics', 'AdminAnalytics'],
      keepUnusedDataFor: 900,
    }),

    // User analytics with registration and activity metrics
    getUserAnalytics: builder.query({
      query: (params = {}) => ({
        url: 'analytics/users',
        params: {
          period: mapPeriodToBackend(params.period || 'month'),
          role: params.role,
          verificationStatus: params.verificationStatus,
          ...params,
        },
      }),
      providesTags: ['UserAnalytics', 'AdminAnalytics'],
      keepUnusedDataFor: 900,
    }),

    // Product analytics with performance and popularity metrics
    getProductAnalyticsV2: builder.query({
      query: (params = {}) => ({
        url: 'analytics/products',
        params: {
          period: mapPeriodToBackend(params.period || 'month'),
          category: params.category,
          vendor: params.vendor,
          ...params,
        },
      }),
      providesTags: ['ProductAnalytics', 'AdminAnalytics'],
      keepUnusedDataFor: 900,
    }),

    // Performance dashboard for comprehensive admin metrics
    getPerformanceDashboard: builder.query({
      query: (params = {}) => ({
        url: 'performance/dashboard',
        params: {
          period: mapPeriodToBackend(params.period || 'month'),
          adminId: params.adminId,
          ...params,
        },
      }),
      providesTags: ['PerformanceMetrics', 'AdminDashboard'],
      keepUnusedDataFor: 300, // 5 minutes cache
    }),

    // Clear analytics cache for fresh data regeneration
    clearAnalyticsCache: builder.mutation({
      query: () => ({
        url: 'analytics/cache',
        method: 'DELETE',
      }),
      invalidatesTags: [
        'AdminAnalytics',
        'SalesAnalytics',
        'UserAnalytics',
        'ProductAnalytics',
      ],
    }),

    // ========================================
    // 2. USER MANAGEMENT ENDPOINTS (6 APIs)
    // ========================================

    // Get all users with advanced filtering and pagination
    getAdminUsersV2: builder.query({
      query: (params = {}) => {
        // Backend only supports: page, limit, role, isActive, search
        // Remove unsupported params: verificationStatus, isApproved, sortBy, sortOrder
        const supportedParams = {
          page: params.page || 1,
          limit: params.limit || 20,
        };

        // Only add optional params if they have values
        if (params.role && params.role !== 'all') {
          supportedParams.role = params.role;
        }
        if (params.search) {
          supportedParams.search = params.search;
        }
        if (typeof params.isActive === 'boolean') {
          supportedParams.isActive = params.isActive;
        }

        return {
          url: 'users',
          params: supportedParams,
        };
      },
      providesTags: (result) => [
        { type: 'AdminUsers', id: 'LIST' },
        ...(Array.isArray(result?.data) ? result.data : []).map(({ _id }) => ({
          type: 'AdminUsers',
          id: _id,
        })),
      ],
    }),

    // Get individual user details with complete profile information
    getAdminUserDetails: builder.query({
      query: (id) => `users/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdminUsers', id }],
    }),

    // Update user profile and account settings
    updateAdminUserV2: builder.mutation({
      query: ({ id, ...userData }) => ({
        url: `users/${id}`,
        method: 'PUT',
        body: userData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminUsers', id },
        { type: 'AdminUsers', id: 'LIST' },
        'UserManagement',
      ],
      // Optimistic update for immediate UI feedback
      onQueryStarted: async (
        { id, ...userData },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          adminApiV2Slice.util.updateQueryData(
            'getAdminUsersV2',
            undefined,
            (draft) => {
              const users = draft?.data;
              if (Array.isArray(users)) {
                const user = users.find((u) => u._id === id);
                if (user) {
                  Object.assign(user, userData);
                }
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Safe delete user with dependency checking
    deleteAdminUserV2: builder.mutation({
      query: (id) => ({
        url: `users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'AdminUsers', id },
        { type: 'AdminUsers', id: 'LIST' },
        'UserManagement',
      ],
    }),

    // Create buyer owner with business entity
    createBuyerOwnerV2: builder.mutation({
      query: (ownerData) => ({
        url: 'buyer-owners',
        method: 'POST',
        body: ownerData,
      }),
      invalidatesTags: ['AdminUsers', 'AdminBuyers', 'UserManagement'],
    }),

    // Create buyer manager under existing buyer
    createBuyerManagerV2: builder.mutation({
      query: (managerData) => ({
        url: 'buyer-managers',
        method: 'POST',
        body: managerData,
      }),
      invalidatesTags: ['AdminUsers', 'AdminBuyers', 'UserManagement'],
    }),

    // ==========================================
    // 3. VENDOR MANAGEMENT ENDPOINTS (6 APIs)
    // ==========================================

    // Get all vendors with business information and verification status
    getAdminVendorsV2: builder.query({
      query: (params = {}) => ({
        url: 'vendors',
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          status: params.status, // pending, approved, rejected
          verificationStatus: params.verificationStatus,
          search: params.search,
          sortBy: params.sortBy || 'createdAt',
          sortOrder: params.sortOrder || 'desc',
          businessType: params.businessType,
          location: params.location,
          ...params,
        },
      }),
      providesTags: (result) => [
        { type: 'AdminVendors', id: 'LIST' },
        ...(result?.data || []).map(({ _id }) => ({
          type: 'AdminVendors',
          id: _id,
        })),
      ],
    }),

    // Get vendor details with business metrics and performance data
    getVendorDetailsV2: builder.query({
      query: (id) => `vendors/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdminVendors', id }],
    }),

    // Update vendor business information and settings
    updateVendorV2: builder.mutation({
      query: ({ id, ...vendorData }) => ({
        url: `vendors/${id}`,
        method: 'PUT',
        body: vendorData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminVendors', id },
        { type: 'AdminVendors', id: 'LIST' },
        'VendorVerification',
      ],
    }),

    // Update vendor verification status (pending, approved, rejected)
    updateVendorVerificationV2: builder.mutation({
      query: ({ id, status, reason }) => ({
        url: `vendors/${id}/verification`,
        method: 'PUT',
        body: { status, reason },
      }),
      invalidatesTags: [
        'AdminVendors',
        'VendorVerification',
        'BusinessVerification',
        'AdminDashboard',
      ],
      // Optimistic update for verification status
      onQueryStarted: async (
        { id, status, reason },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          adminApiV2Slice.util.updateQueryData(
            'getAdminVendorsV2',
            undefined,
            (draft) => {
              const vendor = draft?.data?.find((v) => v._id === id);
              if (vendor) {
                vendor.verificationStatus = status;
                vendor.adminNotes = reason;
                vendor.verificationDate =
                  status === 'approved' ? new Date().toISOString() : null;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Deactivate vendor with business impact analysis
    deactivateVendorV2: builder.mutation({
      query: ({ id, reason }) => ({
        url: `vendors/${id}/deactivate`,
        method: 'PUT',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminVendors', id },
        { type: 'AdminVendors', id: 'LIST' },
        'VendorVerification',
      ],
    }),

    // Safe delete vendor with dependency checking
    safeDeleteVendorV2: builder.mutation({
      query: ({ id, reason }) => ({
        url: `vendors/${id}/safe-delete`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: ['AdminVendors', 'VendorVerification'],
    }),

    // ===============================================
    // 4. BUYER MANAGEMENT ENDPOINTS (6 APIs)
    // ===============================================

    // Get all buyers with business information
    getAdminBuyersV2: builder.query({
      query: (params = {}) => ({
        url: 'buyers',
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          status: params.status, // pending, approved, rejected
          verificationStatus: params.verificationStatus,
          search: params.search,
          sortBy: params.sortBy || 'createdAt',
          sortOrder: params.sortOrder || 'desc',
          businessType: params.businessType,
          location: params.location,
          ...params,
        },
      }),
      providesTags: (result) => [
        { type: 'AdminBuyers', id: 'LIST' },
        ...(result?.data || []).map(({ _id }) => ({
          type: 'AdminBuyers',
          id: _id,
        })),
      ],
    }),

    // Get buyer details with ordering history and metrics
    getBuyerDetailsV2: builder.query({
      query: (id) => `buyers/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdminBuyers', id }],
    }),

    // Update buyer business information
    updateBuyerV2: builder.mutation({
      query: ({ id, ...buyerData }) => ({
        url: `buyers/${id}`,
        method: 'PUT',
        body: buyerData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminBuyers', id },
        { type: 'AdminBuyers', id: 'LIST' },
        'BuyerVerification',
      ],
    }),

    // Update buyer verification status
    updateBuyerVerificationV2: builder.mutation({
      query: ({ id, status, reason }) => ({
        url: `buyers/${id}/verification`,
        method: 'PUT',
        body: { status, reason },
      }),
      invalidatesTags: [
        'AdminBuyers',
        'BuyerVerification',
        'BusinessVerification',
        'AdminDashboard',
      ],
      // Optimistic update for verification status
      onQueryStarted: async (
        { id, status, reason },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          adminApiV2Slice.util.updateQueryData(
            'getAdminBuyersV2',
            undefined,
            (draft) => {
              const buyer = draft?.data?.find((r) => r._id === id);
              if (buyer) {
                buyer.verificationStatus = status;
                buyer.adminNotes = reason;
                buyer.verificationDate =
                  status === 'approved' ? new Date().toISOString() : null;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Deactivate buyer with order impact analysis
    deactivateBuyerV2: builder.mutation({
      query: ({ id, reason }) => ({
        url: `buyers/${id}/deactivate`,
        method: 'PUT',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminBuyers', id },
        { type: 'AdminBuyers', id: 'LIST' },
        'BuyerVerification',
      ],
    }),

    // Safe delete buyer with dependency checking
    safeDeleteBuyerV2: builder.mutation({
      query: ({ id, reason }) => ({
        url: `buyers/${id}/safe-delete`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: ['AdminBuyers', 'BuyerVerification'],
    }),

    // ==========================================
    // 5. PRODUCT MANAGEMENT ENDPOINTS (5 APIs)
    // ==========================================

    // Get all products with inventory and performance data
    getAdminProductsV2: builder.query({
      query: (params = {}) => ({
        url: 'products',
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          category: params.category,
          vendor: params.vendor,
          status: params.status,
          search: params.search,
          sortBy: params.sortBy || 'createdAt',
          sortOrder: params.sortOrder || 'desc',
          priceRange: params.priceRange,
          availability: params.availability,
          ...params,
        },
      }),
      providesTags: (result) => [
        { type: 'AdminProducts', id: 'LIST' },
        ...(result?.data?.products || []).map(({ _id }) => ({
          type: 'AdminProducts',
          id: _id,
        })),
      ],
    }),

    // Get product details with analytics and listing information
    getAdminProductDetails: builder.query({
      query: (id) => `products/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdminProducts', id }],
    }),

    // Create new product with image upload support
    createAdminProductV2: builder.mutation({
      query: (productData) => ({
        url: 'products',
        method: 'POST',
        body: productData, // FormData for image uploads
        // Let browser set Content-Type for FormData
      }),
      invalidatesTags: [{ type: 'AdminProducts', id: 'LIST' }],
    }),

    // Update product information and images
    updateAdminProductV2: builder.mutation({
      query: ({ id, formData }) => ({
        url: `products/${id}`,
        method: 'PUT',
        body: formData, // FormData for optional image updates
        // Let browser set Content-Type for FormData
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminProducts', id },
        { type: 'AdminProducts', id: 'LIST' },
      ],
    }),

    // Safe delete product with usage dependency checking
    safeDeleteProductV2: builder.mutation({
      query: ({ id, reason }) => ({
        url: `products/${id}/safe-delete`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: ['AdminProducts'],
    }),

    // Get product statistics and analytics
    getProductStats: builder.query({
      query: () => 'products/stats',
      providesTags: ['AdminProducts'],
    }),

    // =============================================
    // 6. CATEGORY MANAGEMENT ENDPOINTS (7 APIs)
    // =============================================

    // Get all categories with hierarchy and usage statistics
    getAdminCategoriesV2: builder.query({
      query: (params = {}) => ({
        url: 'categories',
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          search: params.search,
          isActive: params.isActive,
          isAvailable: params.isAvailable,
          level: params.level,
          parentCategory: params.parentCategory,
          sortBy: params.sortBy || 'name',
          sortOrder: params.sortOrder || 'asc',
          ...params,
        },
      }),
      providesTags: (result) => [
        { type: 'AdminCategories', id: 'LIST' },
        ...(result?.data || []).map(({ _id }) => ({
          type: 'AdminCategories',
          id: _id,
        })),
      ],
    }),

    // Get category details with products and performance metrics
    getAdminCategoryDetails: builder.query({
      query: (id) => `categories/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdminCategories', id }],
    }),

    // Get category usage statistics and analytics
    getCategoryUsageStats: builder.query({
      query: (id) => `categories/${id}/usage`,
      providesTags: (result, error, id) => [{ type: 'CategoryUsage', id }],
      // Cache usage stats for 30 minutes
      keepUnusedDataFor: 1800,
    }),

    // Create new category with image and hierarchy support
    createAdminCategoryV2: builder.mutation({
      query: (categoryData) => ({
        url: 'categories',
        method: 'POST',
        body: categoryData, // FormData for image uploads
        // Let browser set Content-Type for FormData
      }),
      invalidatesTags: [
        { type: 'AdminCategories', id: 'LIST' },
        'AdminCategories',
      ],
    }),

    // Update category information and hierarchy
    updateAdminCategoryV2: builder.mutation({
      query: ({ id, formData }) => ({
        url: `categories/${id}`,
        method: 'PUT',
        body: formData, // FormData for optional image updates
        // Let browser set Content-Type for FormData
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminCategories', id },
        { type: 'AdminCategories', id: 'LIST' },
        'AdminCategories',
      ],
    }),

    // Toggle category availability (flag/unflag system)
    toggleCategoryAvailabilityV2: builder.mutation({
      query: ({ id, isAvailable, flagReason }) => ({
        url: `categories/${id}/availability`,
        method: 'PUT',
        body: { isAvailable, flagReason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminCategories', id },
        { type: 'AdminCategories', id: 'LIST' },
        'ContentModeration',
      ],
    }),

    // Safe delete category with product dependency checking
    safeDeleteCategoryV2: builder.mutation({
      query: ({ id, reason }) => ({
        url: `categories/${id}/safe-delete`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: ['AdminCategories'],
    }),

    // =============================================
    // 7. MARKET MANAGEMENT ENDPOINTS (7 APIs)
    // =============================================

    // Get all markets with filtering and statistics
    getAdminMarkets: builder.query({
      query: (params = {}) => ({
        url: 'markets',
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          search: params.search,
          status: params.status, // active, inactive, flagged
          city: params.city,
          sortBy: params.sortBy || 'name',
          sortOrder: params.sortOrder || 'asc',
          ...params,
        },
      }),
      providesTags: (result) => [
        { type: 'AdminMarkets', id: 'LIST' },
        ...(result?.data || []).map(({ _id }) => ({
          type: 'AdminMarkets',
          id: _id,
        })),
      ],
    }),

    // Get market details with usage statistics
    getMarketDetails: builder.query({
      query: (id) => `markets/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdminMarkets', id }],
    }),

    // Get market usage statistics
    getMarketUsageStats: builder.query({
      query: (id) => `markets/${id}/usage`,
      providesTags: (result, error, id) => [{ type: 'MarketUsage', id }],
      keepUnusedDataFor: 1800, // Cache for 30 minutes
    }),

    // Create new market with image
    createMarket: builder.mutation({
      query: (marketData) => ({
        url: 'markets',
        method: 'POST',
        body: marketData, // FormData for image uploads
      }),
      invalidatesTags: [
        { type: 'AdminMarkets', id: 'LIST' },
        'AdminMarkets',
      ],
    }),

    // Update market information
    updateMarket: builder.mutation({
      query: ({ id, formData }) => ({
        url: `markets/${id}`,
        method: 'PUT',
        body: formData, // FormData for optional image updates
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminMarkets', id },
        { type: 'AdminMarkets', id: 'LIST' },
        'AdminMarkets',
        'AdminVendors', // Invalidate vendors since they reference markets
      ],
    }),

    // Toggle market availability (flag/unflag system)
    toggleMarketAvailability: builder.mutation({
      query: ({ id, isAvailable, flagReason }) => ({
        url: `markets/${id}/availability`,
        method: 'PUT',
        body: { isAvailable, flagReason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminMarkets', id },
        { type: 'AdminMarkets', id: 'LIST' },
        'ContentModeration',
        'AdminVendors', // Vendors might be affected
      ],
    }),

    // Safe delete market with vendor dependency checking
    safeDeleteMarket: builder.mutation({
      query: ({ id }) => ({
        url: `markets/${id}/safe-delete`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminMarkets', 'AdminVendors'],
    }),

    // ===========================================
    // 8. LISTING MANAGEMENT ENDPOINTS (9 APIs)
    // ===========================================

    // Get all admin listings with advanced filtering
    getAdminListings: builder.query({
      query: (params = {}) => ({
        url: 'listings',
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          status: params.status, // active, inactive, out_of_stock, discontinued
          featured: params.featured, // boolean
          isFlagged: params.isFlagged, // boolean
          vendorId: params.vendorId,
          productId: params.productId,
          search: params.search,
          sortBy: params.sortBy || 'createdAt',
          sortOrder: params.sortOrder || 'desc',
          minPrice: params.minPrice,
          maxPrice: params.maxPrice,
          ...params,
        },
      }),
      providesTags: (result) => [
        { type: 'AdminListings', id: 'LIST' },
        ...(result?.data || []).map(({ _id }) => ({
          type: 'AdminListings',
          id: _id,
        })),
      ],
    }),

    // Get featured listings for moderation
    getFeaturedListings: builder.query({
      query: (params = {}) => ({
        url: 'listings/featured',
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          sortBy: params.sortBy || 'createdAt',
          sortOrder: params.sortOrder || 'desc',
          ...params,
        },
      }),
      providesTags: ['AdminListings', 'ContentModeration'],
    }),

    // Get flagged listings for review
    getFlaggedListings: builder.query({
      query: (params = {}) => ({
        url: 'listings/flagged',
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          sortBy: params.sortBy || 'createdAt',
          sortOrder: params.sortOrder || 'desc',
          ...params,
        },
      }),
      providesTags: ['AdminListings', 'ContentModeration'],
    }),

    // Get single listing details with analytics
    getAdminListingDetails: builder.query({
      query: (id) => `listings/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdminListings', id }],
    }),

    // Update listing status (active, inactive, out_of_stock, discontinued)
    updateListingStatus: builder.mutation({
      query: ({ id, status, reason }) => ({
        url: `listings/${id}/status`,
        method: 'PUT',
        body: { status, reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminListings', id },
        { type: 'AdminListings', id: 'LIST' },
        'ContentModeration',
      ],
      // Optimistic update for status change
      onQueryStarted: async (
        { id, status },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          adminApiV2Slice.util.updateQueryData(
            'getAdminListings',
            undefined,
            (draft) => {
              const listing = draft?.data?.find((l) => l._id === id);
              if (listing) {
                listing.status = status;
                listing.updatedAt = new Date().toISOString();
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Toggle listing featured status
    toggleListingFeatured: builder.mutation({
      query: ({ id }) => ({
        url: `listings/${id}/featured`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminListings', id },
        { type: 'AdminListings', id: 'LIST' },
        'ContentModeration',
      ],
      // Optimistic update for featured toggle
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          adminApiV2Slice.util.updateQueryData(
            'getAdminListings',
            undefined,
            (draft) => {
              const listing = draft?.data?.find((l) => l._id === id);
              if (listing) {
                listing.featured = !listing.featured;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Update listing flag status
    updateListingFlag: builder.mutation({
      query: ({ id, isFlagged, flagReason }) => ({
        url: `listings/${id}/flag`,
        method: 'PUT',
        body: { isFlagged, flagReason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminListings', id },
        { type: 'AdminListings', id: 'LIST' },
        'ContentModeration',
      ],
    }),

    // Soft delete listing
    softDeleteListing: builder.mutation({
      query: ({ id, reason }) => ({
        url: `listings/${id}`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: ['AdminListings', 'ContentModeration'],
    }),

    // Bulk update listings (activate, deactivate, delete, approve, reject)
    bulkUpdateListings: builder.mutation({
      query: ({ ids, action, reason }) => ({
        url: 'listings/bulk',
        method: 'POST',
        body: { ids, action, reason },
      }),
      invalidatesTags: [
        'AdminListings',
        'ContentModeration',
        'AdminDashboard',
      ],
    }),

    // =========================================
    // 8. BULK OPERATIONS & ADVANCED FEATURES
    // =========================================

    // Bulk update verification status for multiple entities
    bulkUpdateVerificationStatus: builder.mutation({
      query: ({ userIds, entityType, status, reason }) => ({
        url: 'bulk/verification',
        method: 'POST',
        body: { userIds, entityType, status, reason },
      }),
      invalidatesTags: [
        'AdminVendors',
        'AdminBuyers',
        'VendorVerification',
        'BuyerVerification',
        'BusinessVerification',
        'AdminDashboard',
      ],
    }),

    // System settings management
    getSystemSettings: builder.query({
      query: (filter = {}) => ({
        url: 'settings',
        params: filter,
      }),
      providesTags: ['SystemSettings'],
    }),

    // Update system setting
    updateSystemSetting: builder.mutation({
      query: ({ key, value, changeReason }) => ({
        url: `settings/key/${key}`,
        method: 'PUT',
        body: { value, changeReason },
      }),
      invalidatesTags: ['SystemSettings'],
    }),

    // ================================================
    // ENHANCED SETTINGS MANAGEMENT ENDPOINTS (Prompt 8)
    // ================================================

    // Get settings by category
    getCategorySettings: builder.query({
      query: (category) => ({
        url: `settings/category/${category}`,
      }),
      providesTags: (result, error, category) => [
        { type: 'SystemSettings', id: category },
        'SystemSettings',
      ],
    }),

    // Get individual setting by key
    getSettingByKey: builder.query({
      query: (key) => ({
        url: `settings/key/${key}`,
      }),
      providesTags: (result, error, key) => [
        { type: 'SystemSettings', id: key },
      ],
    }),

    // Get settings history for audit trail
    getSettingHistory: builder.query({
      query: ({
        key,
        category,
        timeRange = '30d',
        type,
        search,
        page = 1,
        limit = 50,
      }) => ({
        url: key ? `settings/key/${key}/history` : 'settings/history',
        params: {
          category,
          timeRange,
          type,
          search,
          page,
          limit,
        },
      }),
      providesTags: (result, error, { key, category }) => [
        { type: 'SettingsHistory', id: key || category || 'all' },
      ],
    }),

    // Bulk update multiple settings
    bulkUpdateSettings: builder.mutation({
      query: ({ settings, changeReason }) => ({
        url: 'settings/bulk',
        method: 'PUT',
        body: { settings, changeReason },
      }),
      invalidatesTags: ['SystemSettings', 'SettingsHistory'],
    }),

    // Reset settings to default values
    resetSystemSetting: builder.mutation({
      query: ({ keys, category, reason }) => ({
        url: 'settings/reset',
        method: 'POST',
        body: { keys, category, reason },
      }),
      invalidatesTags: ['SystemSettings', 'SettingsHistory'],
    }),

    // Validate setting value
    validateSetting: builder.mutation({
      query: ({ key, value }) => ({
        url: `settings/key/${key}/validate`,
        method: 'POST',
        body: { value },
      }),
    }),

    // Get settings schema/configuration
    getSettingsSchema: builder.query({
      query: (category) => ({
        url: 'settings/schema',
        params: { category },
      }),
      providesTags: ['SettingsSchema'],
    }),

    // Import settings from file
    importSettings: builder.mutation({
      query: ({ settings, importMode = 'merge', changeReason }) => ({
        url: 'settings/import',
        method: 'POST',
        body: { settings, importMode, changeReason },
      }),
      invalidatesTags: ['SystemSettings', 'SettingsHistory'],
    }),

    // Export settings
    exportSettings: builder.query({
      query: ({ category, format = 'json', includeDefaults = false }) => ({
        url: 'settings/export',
        params: { category, format, includeDefaults },
      }),
    }),

    // Get settings templates
    getSettingsTemplates: builder.query({
      query: () => ({
        url: 'settings/templates',
      }),
      providesTags: ['SettingsTemplates'],
    }),

    // Apply settings template
    applySettingsTemplate: builder.mutation({
      query: ({ templateId, overrides = {}, changeReason }) => ({
        url: `settings/templates/${templateId}/apply`,
        method: 'POST',
        body: { overrides, changeReason },
      }),
      invalidatesTags: ['SystemSettings', 'SettingsHistory'],
    }),

    // Performance monitoring and SLA tracking
    getPerformanceMetrics: builder.query({
      query: (params = {}) => ({
        url: 'performance/metrics',
        params,
      }),
      providesTags: ['PerformanceMetrics'],
      // Auto-refresh performance metrics every 2 minutes
      // Polling disabled for MVP
    }),

    // ================================================
    // 8. ENHANCED ANALYTICS ENDPOINTS (Prompt 7)
    // ================================================

    // Get comprehensive product analytics with inventory insights
    getProductAnalytics: builder.query({
      query: (params = {}) => ({
        url: 'analytics/products/comprehensive',
        params: {
          timeRange: params.timeRange || '30d',
          category: params.category,
          vendor: params.vendor,
          includeInventory: params.includeInventory !== false,
          includeTrends: params.includeTrends !== false,
          ...params,
        },
      }),
      providesTags: ['ProductAnalytics', 'AdminAnalytics'],
      keepUnusedDataFor: 600, // 10 minutes
    }),

    // ================================================
    // 9. PERFORMANCE MONITORING ENDPOINTS (Prompt 7)
    // ================================================

    // Get performance overview with system health metrics
    getPerformanceOverview: builder.query({
      query: (params = {}) => ({
        url: 'performance/overview',
        params: {
          timeRange: params.timeRange || '24h',
          includeAlerts: params.includeAlerts !== false,
          includeSystemHealth: params.includeSystemHealth !== false,
          ...params,
        },
      }),
      providesTags: ['PerformanceMetrics'],
      // Refresh every minute for real-time monitoring
      // Polling disabled for MVP
    }),

    // Get admin team performance metrics and efficiency tracking
    getAdminPerformance: builder.query({
      query: (params = {}) => ({
        url: 'performance/admin-team',
        params: {
          timeRange: params.timeRange || '24h',
          department: params.department,
          includeIndividual: params.includeIndividual !== false,
          includeWorkload: params.includeWorkload !== false,
          ...params,
        },
      }),
      providesTags: ['PerformanceMetrics', 'AdminUsers'],
      keepUnusedDataFor: 300, // 5 minutes
    }),

    // Get SLA metrics and compliance tracking
    getSLAMetrics: builder.query({
      query: (params = {}) => ({
        url: 'performance/sla',
        params: {
          timeRange: params.timeRange || '24h',
          includeViolations: params.includeViolations !== false,
          severity: params.severity, // critical, high, medium, low
          status: params.status, // active, resolved
          ...params,
        },
      }),
      providesTags: ['PerformanceMetrics'],
      // Refresh SLA data every 2 minutes
      // Polling disabled for MVP
    }),

    // Create or update SLA violation
    updateSLAViolation: builder.mutation({
      query: ({ violationId, status, resolution, assignee }) => ({
        url: `performance/sla/violations/${violationId}`,
        method: 'PUT',
        body: { status, resolution, assignee },
      }),
      invalidatesTags: ['PerformanceMetrics'],
    }),

    // ================================================
    // 10. REPORT GENERATION ENDPOINTS (Prompt 7)
    // ================================================

    // Generate analytics report with custom parameters
    generateAnalyticsReport: builder.mutation({
      query: (reportConfig) => ({
        url: 'reports/analytics/generate',
        method: 'POST',
        body: {
          template: reportConfig.template || 'comprehensive',
          format: reportConfig.format || 'pdf', // pdf, csv, both
          timeRange: reportConfig.timeRange || '30d',
          sections: reportConfig.sections || [
            'overview',
            'sales',
            'users',
            'products',
          ],
          includeCharts: reportConfig.includeCharts !== false,
          includeRawData: reportConfig.includeRawData !== false,
          recipientEmail: reportConfig.recipientEmail,
          customFilters: reportConfig.customFilters,
          ...reportConfig,
        },
      }),
      // Don't cache report generation
      invalidatesTags: [],
    }),

    // Schedule recurring analytics reports
    scheduleAnalyticsReport: builder.mutation({
      query: (scheduleConfig) => ({
        url: 'reports/analytics/schedule',
        method: 'POST',
        body: {
          schedule: scheduleConfig.schedule, // daily, weekly, monthly
          template: scheduleConfig.template || 'comprehensive',
          format: scheduleConfig.format || 'pdf',
          sections: scheduleConfig.sections,
          recipients: scheduleConfig.recipients || [],
          timeZone: scheduleConfig.timeZone || 'UTC',
          isActive: scheduleConfig.isActive !== false,
          ...scheduleConfig,
        },
      }),
      invalidatesTags: ['SystemSettings'],
    }),

    // Get report generation history and status
    getReportHistory: builder.query({
      query: (params = {}) => ({
        url: 'reports/history',
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          type: params.type, // analytics, performance
          status: params.status, // pending, completed, failed
          ...params,
        },
      }),
      providesTags: ['SystemSettings'],
      keepUnusedDataFor: 300,
    }),

    // Reset System Settings - Restore defaults
    resetSystemSettings: builder.mutation({
      query: ({ category, confirm = false }) => ({
        url: '/system/settings/reset',
        method: 'POST',
        body: {
          category,
          confirm,
          resetTimestamp: new Date().toISOString(),
        },
      }),
      invalidatesTags: ['SystemSettings', 'AdminDashboard'],
    }),
  }),
});

// Export hooks for all endpoints
export const {
  // Dashboard & Analytics
  useGetDashboardOverviewQuery,
  useGetAnalyticsOverviewQuery,
  useGetSalesAnalyticsQuery,
  useGetUserAnalyticsQuery,
  useGetProductAnalyticsV2Query,
  useClearAnalyticsCacheMutation,

  // User Management
  useGetAdminUsersV2Query,
  useGetAdminUserDetailsQuery,
  useUpdateAdminUserV2Mutation,
  useDeleteAdminUserV2Mutation,
  useCreateBuyerOwnerV2Mutation,
  useCreateBuyerManagerV2Mutation,

  // Vendor Management
  useGetAdminVendorsV2Query,
  useGetVendorDetailsV2Query,
  useUpdateVendorV2Mutation,
  useUpdateVendorVerificationV2Mutation,
  useDeactivateVendorV2Mutation,
  useSafeDeleteVendorV2Mutation,

  // Buyer Management
  useGetAdminBuyersV2Query,
  useGetBuyerDetailsV2Query,
  useUpdateBuyerV2Mutation,
  useUpdateBuyerVerificationV2Mutation,
  useDeactivateBuyerV2Mutation,
  useSafeDeleteBuyerV2Mutation,

  // Product Management
  useGetAdminProductsV2Query,
  useGetAdminProductDetailsQuery,
  useCreateAdminProductV2Mutation,
  useUpdateAdminProductV2Mutation,
  useSafeDeleteProductV2Mutation,
  useGetProductStatsQuery,

  // Category Management
  useGetAdminCategoriesV2Query,
  useGetAdminCategoryDetailsQuery,
  useGetCategoryUsageStatsQuery,
  useCreateAdminCategoryV2Mutation,
  useUpdateAdminCategoryV2Mutation,
  useToggleCategoryAvailabilityV2Mutation,
  useSafeDeleteCategoryV2Mutation,

  // Market Management
  useGetAdminMarketsQuery,
  useGetMarketDetailsQuery,
  useGetMarketUsageStatsQuery,
  useCreateMarketMutation,
  useUpdateMarketMutation,
  useToggleMarketAvailabilityMutation,
  useSafeDeleteMarketMutation,

  // Listing Management
  useGetAdminListingsQuery,
  useGetFeaturedListingsQuery,
  useGetFlaggedListingsQuery,
  useGetAdminListingDetailsQuery,
  useUpdateListingStatusMutation,
  useToggleListingFeaturedMutation,
  useUpdateListingFlagMutation,
  useSoftDeleteListingMutation,
  useBulkUpdateListingsMutation,

  // Advanced Features
  useBulkUpdateVerificationStatusMutation,
  useGetSystemSettingsQuery,
  useUpdateSystemSettingMutation,
  useGetPerformanceMetricsQuery,

  // Enhanced Settings Management (Prompt 8)
  useGetCategorySettingsQuery,
  useGetSettingByKeyQuery,
  useGetSettingHistoryQuery,
  useBulkUpdateSettingsMutation,
  useResetSystemSettingMutation,
  useValidateSettingMutation,
  useGetSettingsSchemaQuery,
  useImportSettingsMutation,
  useExportSettingsQuery,
  useGetSettingsTemplatesQuery,
  useApplySettingsTemplateMutation,

  // Enhanced Analytics (Prompt 7)
  useGetProductAnalyticsQuery,

  // Performance Monitoring (Prompt 7)
  useGetPerformanceOverviewQuery,
  useGetPerformanceDashboardQuery,
  useGetAdminPerformanceQuery,
  useGetSLAMetricsQuery,
  useUpdateSLAViolationMutation,

  // Report Generation (Prompt 7)
  useGenerateAnalyticsReportMutation,
  useScheduleAnalyticsReportMutation,
  useGetReportHistoryQuery,

  // System Settings
  useResetSystemSettingsMutation,
} = adminApiV2Slice;

export default adminApiV2Slice;
